import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useCms } from '../context/CmsContext';

const tabs = [
  { id: 'all', label: 'Alle billeder' },
  { id: 'facader', label: 'Facader', category: 'Facader' },
  { id: 'graffiti', label: 'Graffiti', category: 'Graffiti' },
  { id: 'natursten', label: 'Natursten', category: 'Natursten' },
  { id: 'industri', label: 'Industri', category: 'Industri' },
  { id: 'metal', label: 'Metal', category: 'Metal' }
];

export default function GalleryPage() {
  const { gallery } = useCms();
  const [galleryItems, setGalleryItems] = useState(gallery || []);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (gallery && gallery.length > 0) {
      setGalleryItems(gallery);
    }
  }, [gallery]);

  // Filter items based on active tab
  const filteredItems = galleryItems.filter((item) => {
    if (activeTab === 'all') return true;
    const currentTab = tabs.find((t) => t.id === activeTab);
    return item.category === currentTab?.category;
  });

  const handlePrev = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev === 0 ? filteredItems.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev === filteredItems.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <Helmet>
        <title>Referencegalleri: Laserrensning af facade og murværk</title>
        <meta name="description" content="Se vores vilde før/efter-billeder af laserrensning af facade, murværk og graffitifjernelse over hele Danmark. Se den skånsomme forskel her." />
        <meta property="og:title" content="Referencegalleri: Laserrensning af facade og murværk" />
        <meta property="og:description" content="Se vores vilde før/efter-billeder af laserrensning af facade, murværk og graffitifjernelse over hele Danmark. Se den skånsomme forskel her." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/sandstensfigurer-kbh-h.jpg" />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/front-kbh-chr-d-9-high.webp?width=1800&quality=80&format=auto"
            alt="Ecolaser baggrund"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-3">
          <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">Laserrensning af Facade & Murværk</h1>
          <p className="text-zinc-200 text-sm md:text-base max-w-2xl leading-relaxed">
            Se vores vilde før/efter-billeder af laserrensning af facade, murværk og graffitifjernelse over hele Danmark. Se den skånsomme forskel her.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-white py-16 min-h-[600px]">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-12">
          {/* Tabs Selector */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 border-b border-zinc-100 pb-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setLightboxIndex(null);
                }}
                className={`px-4 py-2.5 rounded-lg text-xs md:text-sm font-semibold uppercase tracking-wider transition-all duration-200 cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-brand-green text-zinc-950 font-bold shadow-md'
                    : 'bg-zinc-50 text-zinc-600 hover:text-zinc-900 border border-zinc-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <i className="fa-solid fa-spinner animate-spin text-brand-green text-4xl" />
              <p className="text-zinc-500 font-mono text-xs">Henter galleribilleder...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">
              <i className="fa-regular fa-image-slash text-4xl mb-4" />
              <p className="text-sm">Ingen billeder fundet i denne kategori.</p>
            </div>
          ) : (
            /* Gallery Grid */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredItems.map((item, idx) => {
                const imgUrl = item.after_image?.[0] || item.before_image?.[0];
                return (
                  <div
                    key={item.id}
                    onClick={() => setLightboxIndex(idx)}
                    className="group bg-zinc-50 border border-zinc-200 hover:border-brand-green/30 rounded-xl overflow-hidden aspect-square cursor-pointer relative transition-all duration-300 hover:-translate-y-1 hover:shadow-sm"
                  >
                    <img
                      src={`${imgUrl}?width=400&height=400&fit=cover&quality=80&format=auto`}
                      alt={item.title?.da}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                      <h4 className="text-white text-xs font-bold truncate">{item.title?.da}</h4>
                      <p className="text-gray-300 text-[10px] truncate mt-1">{item.description?.da}</p>
                      <span className="text-brand-green text-[9px] uppercase tracking-wider font-semibold mt-2 inline-flex items-center gap-1">
                        {item.category} <i className="fa-solid fa-expand text-[8px]" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox with Navigation */}
      {lightboxIndex !== null && filteredItems[lightboxIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4 select-none"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-6 right-6 text-white hover:text-brand-green text-3xl focus:outline-none cursor-pointer z-50"
            aria-label="Close lightbox"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {/* Prev Button */}
          <button
            onClick={handlePrev}
            className="absolute left-6 text-white hover:text-brand-green text-4xl focus:outline-none cursor-pointer z-50 p-4"
            aria-label="Previous image"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>

          {/* Next Button */}
          <button
            onClick={handleNext}
            className="absolute right-6 text-white hover:text-brand-green text-4xl focus:outline-none cursor-pointer z-50 p-4"
            aria-label="Next image"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>

          {/* Lightbox Content */}
          <div className="max-w-4xl w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={filteredItems[lightboxIndex].after_image?.[0] || filteredItems[lightboxIndex].before_image?.[0]}
              alt={filteredItems[lightboxIndex].title?.da}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="text-center max-w-xl">
              <h3 className="text-white text-lg font-bold">{filteredItems[lightboxIndex].title?.da}</h3>
              <p className="text-gray-300 text-xs md:text-sm mt-1">{filteredItems[lightboxIndex].description?.da}</p>
              <span className="inline-block px-2.5 py-1 rounded bg-brand-green/10 text-brand-green text-[10px] font-bold uppercase tracking-wider mt-2">
                {filteredItems[lightboxIndex].category}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
